// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { FormFieldStyle } from "@insite/mobius/FormField";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import { Typography } from "@insite/mobius/index";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import cartLineSharedStyles from "@stanion/App/SharedStyle/Cart/CartLineSharedStyles";
import { color, hideInputNumberArrows, maxLengthAttr, mergeWithCss, xMask } from "@stanion/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

type ButtonType = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "disabled">;
interface OwnProps extends Omit<TextFieldProps, "css"> {
    decreaseButtonProps?: ButtonType;
    increaseButtonProps?: ButtonType;
    showActionButton?: boolean;
    showMobileLabel?: boolean;
    label?: string;
    editable?: boolean;
    extendedStyles?: XngCartLineQuantityStyles;
}

type Props = OwnProps;

export interface XngCartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    wrapper?: InjectableCss;
    countButton?: ButtonPresentationProps;
    increaseButton?: ButtonPresentationProps;
    decreaseButton?: ButtonPresentationProps;
    countIcon?: IconPresentationProps;
    qtyLabel?: TypographyProps;
    valueText?: TypographyProps;
}

export const xngCartLineQuantityStyles: XngCartLineQuantityStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            position: relative;
            border: 1px solid ${color("common.ui5")};
            height: 30px;
            max-width: 90px;
            min-width: 90px;
            ${({ showActionButton }: any) =>
                showActionButton &&
                css`
                    & > ${FormFieldStyle} {
                        padding-left: 27px;
                        padding-right: 27px;
                    }
                `}
        `,
    },
    editableQuantityTextField: {
        mergeCss: true,
        backgroundColor: "transparent",
        sizeVariant: "small",
        labelProps: {
            css: css`
                display: none;
            `,
        },
        cssOverrides: {
            formField: css`
                && input:not(.react-datetime-picker__inputGroup__input):not([name="datetime"]) {
                    &,
                    &:active,
                    &:hover,
                    &:focus {
                        outline: none;
                        border: 0;
                        box-shadow: none;
                        &:not(:disabled) {
                            border: 0;
                            box-shadow: none;
                        }
                    }
                }
            `,
            formInputWrapper: css`
                &&&& {
                    margin-top: 0;
                }
            `,

            inputSelect: css`
                font-size: 14px;
                line-height: 16px;
                text-align: center;
                padding: 0 !important;
                height: 26px;
                border: none;
                box-shadow: none !important;
                &:hover,
                &:focus {
                    border: none;
                    box-shadow: none !important;
                }
                &:disabled {
                    background-color: transparent;
                }

                ${hideInputNumberArrows}
            `,
        },
    },
    countButton: {
        variant: "tertiary",
        sizeVariant: "small",
        css: css`
            position: absolute;
            top: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 26px;
            height: 26px;
            flex: 0 0 26px;
            padding: 0;
            border: 0;
            &:hover:not(:disabled) {
                ${IconWrapper} {
                    color: ${color("common.hover")};
                }
            }
        `,
    },
    countIcon: {
        color: "common.ui5",
        size: 14,
        mergeCss: true,
    },
    qtyLabel: {
        ...cartLineSharedStyles.qtyLabel,
    },
    valueText: {
        variant: "h6",
        as: "p",
    },
};

xngCartLineQuantityStyles.decreaseButton = mergeWithCss(xngCartLineQuantityStyles.countButton, {
    css: css`
        left: 0;
    `,
});

xngCartLineQuantityStyles.increaseButton = mergeWithCss(xngCartLineQuantityStyles.countButton, {
    css: css`
        right: 0;
    `,
});

const enterKey = 13;

const XngCartLineQuantity: FC<Props> = ({
    extendedStyles,
    decreaseButtonProps,
    increaseButtonProps,
    disabled: disabledProps,
    value: currentValue,
    onChange: onChangeTextField,
    onBlur: onBlurTextField,
    onClick: onClickTextField,
    onKeyDown: onKeyDownTextField,
    showActionButton = true,
    editable = true,
    showMobileLabel = true,
    label,
    ...qtyFieldsProps
}) => {
    const [styles] = useState(() => mergeWithCss(xngCartLineQuantityStyles, extendedStyles));
    const [callOnSubmitSafety, setCallOnSubmitSafety] = useState(false);

    const disabled = !callOnSubmitSafety && disabledProps;

    /**
     * UseEffect to wait until data change in cartQuantity state
     * then we do submit
     * src/FrontEnd/modules/client-framework/src/Common/Hooks/useAccessibleSubmit.ts
     */
    useEffect(() => {
        if (callOnSubmitSafety) {
            onBlurHandler();
            setCallOnSubmitSafety(false);
        }
    }, [currentValue]);

    const onClickHandler = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        event.currentTarget.focus();
        onClickTextField?.(event);
    };

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.currentTarget.value = !event.currentTarget.value ? "" : xMask.qty(event.currentTarget.value);
        if (event.currentTarget.value.length > maxLengthAttr.qty) {
            event.currentTarget.value = `${currentValue}`;
        }

        onChangeTextField?.(event);
    };

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === enterKey && !onKeyDownTextField) {
            event.stopPropagation();
            onBlurTextField?.(event as any);
        }
    };

    const onBlurHandler = (event?: React.ChangeEvent<HTMLInputElement>) => {
        /**
         * We skip this validation when a user click on increase/decrease button
         * In this case, event.currentTarget will be equal null
         */
        if (event?.currentTarget) {
            event.currentTarget.value = Number(xMask.qty(event.currentTarget.value || "0")) as any;
            onChangeHandler(event);
        }
        onBlurTextField?.(event as any);
    };

    const onDecreaseClickHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (disabled) {
            return;
        }

        const nextValue =
            currentValue && isNumeric(`${currentValue}`) && Number(currentValue) - 1 >= 0
                ? Number(currentValue) - 1
                : undefined;

        if (!decreaseButtonProps?.onClick) {
            if (!nextValue) {
                return;
            }

            event.currentTarget.value = xMask.qty(`${nextValue}`);
            onChangeHandler(event as any);
            setCallOnSubmitSafety(true);
            return;
        }

        decreaseButtonProps?.onClick?.(event);
    };

    const onIncreaseClickHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (disabled) {
            return;
        }

        const nextValue =
            currentValue && isNumeric(`${currentValue}`) && `${Number(currentValue) + 1}`.length <= maxLengthAttr.qty
                ? Number(currentValue) + 1
                : undefined;

        if (!increaseButtonProps?.onClick) {
            if (!nextValue) {
                return;
            }

            event.currentTarget.value = xMask.qty(`${nextValue}`);
            onChangeHandler(event as any);
            setCallOnSubmitSafety(true);
            return;
        }

        increaseButtonProps?.onClick?.(event);
    };

    return (
        <>
            {showMobileLabel && <Typography {...styles.qtyLabel}>{label || translate("QTY")}</Typography>}
            {editable && !disabled ? (
                <StyledWrapper
                    {...styles.wrapper}
                    {...{ showActionButton, disabled }}
                    data-test-selectore="quantity-field"
                >
                    {showActionButton && (
                        <Button
                            {...decreaseButtonProps}
                            {...styles.decreaseButton}
                            onClick={onDecreaseClickHandler}
                            disabled={disabled || Number(currentValue) <= 1}
                        >
                            <Icon {...styles.countIcon} src={Minus} />
                        </Button>
                    )}
                    <TextField
                        type="number"
                        maxLength={maxLengthAttr.qty}
                        disabled={disabled}
                        value={currentValue}
                        onChange={onChangeHandler}
                        onBlur={onBlurHandler}
                        onClick={onClickHandler}
                        onKeyDown={onKeyDownHandler}
                        {...styles.editableQuantityTextField}
                        {...qtyFieldsProps}
                    />
                    {showActionButton && (
                        <Button
                            {...increaseButtonProps}
                            {...styles.increaseButton}
                            disabled={disabled || Number(currentValue) > 999999}
                            onClick={onIncreaseClickHandler}
                        >
                            <Icon {...styles.countIcon} src={Plus} />
                        </Button>
                    )}
                </StyledWrapper>
            ) : (
                <>
                    <VisuallyHidden as="label">{translate("QTY")}</VisuallyHidden>
                    <Typography {...styles.valueText}>{currentValue}</Typography>
                </>
            )}
        </>
    );
};

export default XngCartLineQuantity;
